import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ClearRoundedIcon from '@mui/icons-material/ClearRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import WorkOutlineRoundedIcon from '@mui/icons-material/WorkOutlineRounded';
import { Snackbar, Alert, CircularProgress } from '@mui/material';
import { setUserDetails } from '../../Redux/Slices/userDetailsSlice';
import { getUserId } from '../../api/index';
import { getProfessions, getUserProfessions, saveUserProfessions } from '../../api/professions';
import Loader from '../Loader';
import './EditProfessions.css';

const EditProfessions = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const userDetails = useSelector((state) => state.userDetails?.userDetails);

    const [selectedTitles, setSelectedTitles] = useState([]);
    const [initialTitles, setInitialTitles] = useState([]);
    const [categoriesData, setCategoriesData] = useState([]);
    const [selectedCategoryTab, setSelectedCategoryTab] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const searchInputRef = useRef(null);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // 1. Initialize user's selected professions
    useEffect(() => {
        if (userDetails?.professions && Array.isArray(userDetails.professions)) {
            setSelectedTitles(userDetails.professions);
            setInitialTitles(userDetails.professions);
        } else {
            const uid = getUserId();
            if (uid) {
                getUserProfessions(uid)
                    .then((res) => {
                        if (res?.success && Array.isArray(res?.data)) {
                            const titles = res.data.map((item) => (typeof item === 'string' ? item : item.title));
                            setSelectedTitles(titles);
                            setInitialTitles(titles);
                        }
                    })
                    .catch(() => {
                        // fallback to empty or userDetails
                    });
            }
        }
    }, [userDetails]);

    // 2. Fetch latest categorized professions catalog from backend
    useEffect(() => {
        setIsLoading(true);
        getProfessions(undefined, undefined, false, 1, 100)
            .then((res) => {
                if (res?.success && Array.isArray(res?.data) && res.data.length > 0) {
                    const formatted = res.data.map((cat) => ({
                        category: cat.category || 'General',
                        titles: (cat.professions || cat.titles || []).map((p) => p.title || p)
                    }));
                    setCategoriesData(formatted);
                }
            })
            .catch((err) => {
                console.warn('Error fetching professions catalog:', err);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar({ ...snackbar, open: false });
    };

    // Toggle Title Selection
    const handleToggleTitle = (title) => {
        const cleanTitle = title.trim();
        setSelectedTitles((prev) => {
            const exists = prev.some((t) => t.toLowerCase() === cleanTitle.toLowerCase());
            if (exists) {
                return prev.filter((t) => t.toLowerCase() !== cleanTitle.toLowerCase());
            } else {
                return [...prev, cleanTitle];
            }
        });
    };

    // Remove single title
    const handleRemoveTitle = (titleToRemove) => {
        setSelectedTitles((prev) =>
            prev.filter((t) => t.toLowerCase() !== titleToRemove.toLowerCase())
        );
    };

    // Clear all
    const handleClearAll = () => {
        setSelectedTitles([]);
    };

    // Category Tabs list
    const categoryTabs = useMemo(() => {
        const cats = categoriesData.map((c) => c.category || 'General').filter(Boolean);
        return ['All', ...new Set(cats)];
    }, [categoriesData]);

    // Filtered Categories and Titles based on search query and category tab
    const filteredCatalog = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();

        return categoriesData
            .map((cat) => {
                const catName = cat.category || 'General';
                if (selectedCategoryTab !== 'All' && catName !== selectedCategoryTab) {
                    return null;
                }

                let titles = cat.titles || [];

                if (query) {
                    titles = titles.filter(
                        (t) =>
                            (t && t.toLowerCase().includes(query)) ||
                            catName.toLowerCase().includes(query)
                    );
                }

                if (titles.length === 0) return null;

                return {
                    category: catName,
                    titles
                };
            })
            .filter(Boolean);
    }, [categoriesData, selectedCategoryTab, searchQuery]);

    const totalMatchingTitles = useMemo(() => {
        return filteredCatalog.reduce((acc, cat) => acc + cat.titles.length, 0);
    }, [filteredCatalog]);

    const removedTitles = useMemo(() => {
        return initialTitles.filter((t) => !selectedTitles.some((s) => s.toLowerCase() === t.toLowerCase()));
    }, [initialTitles, selectedTitles]);

    const addedTitles = useMemo(() => {
        return selectedTitles.filter((s) => !initialTitles.some((t) => t.toLowerCase() === s.toLowerCase()));
    }, [initialTitles, selectedTitles]);

    // Save user professions
    const handleSave = async () => {
        if (isSaving) return;
        setIsSaving(true);
        const uid = getUserId();

        try {
            const response = await saveUserProfessions(selectedTitles, uid);

            if (response?.success) {
                const updatedList = response.data || selectedTitles;
                dispatch(setUserDetails({ ...userDetails, professions: updatedList }));

                const communityCount = selectedTitles.length;
                showSnackbar(
                    communityCount > 0
                        ? `Professions saved! ${communityCount} title${communityCount > 1 ? 's' : ''} updated and community circles unlocked.`
                        : 'Professions updated successfully!',
                    'success'
                );

                setTimeout(() => {
                    navigate('/profile-details');
                }, 1200);
            } else {
                showSnackbar(response?.message || 'Failed to update professions. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error updating professions:', error);
            showSnackbar('An error occurred while saving your professions. Please try again.', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading && categoriesData.length === 0) {
        return (
            <div className="EditProfessions">
                <Loader />
            </div>
        );
    }

    return (
        <div className="EditProfessions">
            <div className="editProfessionsContainer">
                <div className="editProfessionsCard">
                    {/* Header */}
                    <div className="editProfessionsHeader">
                        <div className="editProfessionsTitleRow">
                            <span className="editProfessionsTitle">
                                <span className="editProfessionsTitleIcon">
                                    <WorkOutlineRoundedIcon sx={{ fontSize: 26 }} />
                                </span>
                                Professions & Titles
                            </span>
                            <span className="selectedCountBadge">
                                <CheckRoundedIcon sx={{ fontSize: 16 }} />
                                {selectedTitles.length} Selected
                            </span>
                        </div>
                        <p className="editProfessionsSubtitle">
                            Select all titles and professions that apply to you. Your selected titles automatically unlock dedicated professional community circles and empower demographic civic insights.
                        </p>
                    </div>

                    {/* Selected Titles Summary Tray */}
                    {selectedTitles.length > 0 && (
                        <div className="selectedProfessionsTray">
                            <div className="selectedTrayHeader">
                                <span>Selected Titles ({selectedTitles.length})</span>
                                <button className="clearAllBtn" onClick={handleClearAll}>
                                    Clear All
                                </button>
                            </div>
                            <div className="selectedChipsList">
                                {selectedTitles.map((title) => (
                                    <span key={title} className="selectedChip">
                                        <span>{title}</span>
                                        <span
                                            className="selectedChipRemove"
                                            onClick={() => handleRemoveTitle(title)}
                                            title="Remove title"
                                        >
                                            <ClearRoundedIcon sx={{ fontSize: 14 }} />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Search Bar */}
                    <div className="professionsSearchBox">
                        <SearchRoundedIcon className="professionsSearchIcon" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="professionsSearchInput"
                            placeholder="Search over 140+ titles or categories (e.g. Doctor, CEO, Engineer, Judge)..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <span
                                className="professionsClearSearch"
                                onClick={() => {
                                    setSearchQuery('');
                                    searchInputRef.current?.focus();
                                }}
                            >
                                <ClearRoundedIcon sx={{ fontSize: 18 }} />
                            </span>
                        )}
                    </div>

                    {/* Category Filter Tabs */}
                    <div className="categoryTabsContainer">
                        {categoryTabs.map((cat) => (
                            <button
                                key={cat}
                                type="button"
                                className={`categoryTab ${selectedCategoryTab === cat ? 'active' : ''}`}
                                onClick={() => setSelectedCategoryTab(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {/* Catalog List */}
                    {isLoading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                            <Loader />
                        </div>
                    ) : filteredCatalog.length === 0 ? (
                        <div className="noProfessionsFound">
                            <WorkOutlineRoundedIcon sx={{ fontSize: 40, opacity: 0.4, color: '#839696' }} />
                            <span>{searchQuery ? `No titles matching "${searchQuery}"` : "No titles available"}</span>
                            <span style={{ fontSize: '13px', color: '#839696' }}>
                                Try searching for another keyword or select a different category above.
                            </span>
                        </div>
                    ) : (
                        <div className="professionsCatalog">
                            {filteredCatalog.map((cat) => (
                                <div key={cat.category} className="professionCategoryGroup">
                                    <div className="professionCategoryHeader">
                                        <span className="professionCategoryName">{cat.category}</span>
                                        <span className="professionCategoryCount">{cat.titles.length} titles</span>
                                    </div>
                                    <div className="professionTitlesGrid">
                                        {cat.titles.map((title) => {
                                            const isSelected = selectedTitles.some(
                                                (t) => t.toLowerCase() === title.toLowerCase()
                                            );
                                            return (
                                                <div
                                                    key={title}
                                                    className={`professionItemCard ${isSelected ? 'selected' : ''}`}
                                                    onClick={() => handleToggleTitle(title)}
                                                >
                                                    <div className="professionItemLeft">
                                                        <div className="professionCheckbox">
                                                            {isSelected && <CheckRoundedIcon sx={{ fontSize: 13 }} />}
                                                        </div>
                                                        <span className="professionItemTitle" title={title}>
                                                            {title}
                                                        </span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Community Transition Notices */}
                    {removedTitles.length > 0 && (
                        <div className="professionsCommunityNotice notice-warning">
                            <span className="noticeIcon">⚠️</span>
                            <span className="noticeText">
                                Removing <strong>{removedTitles.join(', ')}</strong> will remove your membership and access from {removedTitles.length > 1 ? 'their respective professional community circles' : 'its dedicated professional community circle'}.
                            </span>
                        </div>
                    )}
                    {addedTitles.length > 0 && (
                        <div className="professionsCommunityNotice notice-info">
                            <span className="noticeIcon">✨</span>
                            <span className="noticeText">
                                Adding <strong>{addedTitles.join(', ')}</strong> will unlock and enroll you into {addedTitles.length > 1 ? 'their dedicated professional communities' : 'its dedicated professional community'}.
                            </span>
                        </div>
                    )}
                    {removedTitles.length === 0 && addedTitles.length === 0 && (
                        <div className="professionsCommunityNotice notice-neutral">
                            <span className="noticeIcon">💼</span>
                            <span className="noticeText">
                                Your selected titles dynamically determine which professional community circles you belong to. Changes take effect immediately upon saving.
                            </span>
                        </div>
                    )}

                    {/* Footer Actions */}
                    <div className="editProfessionsFooter">
                        <button
                            type="button"
                            className="cancelProfessionsBtn"
                            onClick={() => navigate('/profile-details')}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="saveProfessionsBtn"
                            onClick={handleSave}
                            disabled={isSaving}
                        >
                            {isSaving ? (
                                <>
                                    <CircularProgress size={16} sx={{ color: '#0E1B1B' }} thickness={5} />
                                    <span>Saving...</span>
                                </>
                            ) : (
                                <>
                                    <CheckRoundedIcon sx={{ fontSize: 18 }} />
                                    <span>Save Professions ({selectedTitles.length})</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '12px' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default EditProfessions;
